<div class="settings-page" data-testid="settings-page">
  <div class="page-header">
    <h1 class="page-header__title">Settings</h1>
    <p class="page-header__subtitle">
      Manage your password, out-of-office dates, and notification preferences.
    </p>
  </div>

  <div class="settings-top-row">
  <!-- Update Password -->
  <section class="card settings-card" data-testid="settings-password-card">
    <div class="settings-card__header">
      <h2 class="settings-card__title">Update Password</h2>
    </div>
    <form [formGroup]="newPassword">
      <div id="new-password" class="settings-password-fields">
        <div class="form-field settings-password-field">
          <label class="form-label" for="currentPassword"
            >Current Password<span class="req">*</span></label
          >
          <div class="settings-password-input">
            <input
              id="currentPassword"
              class="input"
              [type]="showCurrentPassword ? 'text' : 'password'"
              formControlName="currentPassword"
              maxlength="100"
              data-testid="settings-current-password"
              (blur)="trimCurrentPassword()"
            />
            <button
              type="button"
              class="settings-show-password"
              data-testid="settings-current-password-toggle"
              (click)="toggleCurrentPasswordVisibility()"
              [attr.aria-label]="
                !showCurrentPassword ? 'Show password' : 'Hide password'
              "
            >
              <span class="material-icons">{{
                !showCurrentPassword ? "visibility" : "visibility_off"
              }}</span>
            </button>
          </div>
          <div
            *ngIf="
              newPassword.controls['currentPassword']?.invalid &&
              (newPassword.controls['currentPassword'].dirty ||
                newPassword.controls['currentPassword'].touched)
            "
          >
            <span
              class="error-message"
              *ngIf="newPassword.controls.currentPassword.errors?.['required']"
            >
              Current password is required.
            </span>
          </div>
        </div>
        <div class="form-field settings-password-field">
          <label class="form-label" for="newPassword"
            >New Password<span class="req">*</span></label
          >
          <div class="settings-password-input">
            <input
              id="newPassword"
              class="input"
              [type]="showNewPassword ? 'text' : 'password'"
              formControlName="newPassword"
              maxlength="100"
              data-testid="settings-new-password"
              (blur)="trimNewPassword()"
            />
            <button
              type="button"
              class="settings-show-password"
              data-testid="settings-new-password-toggle"
              (click)="toggleNewPasswordVisibility()"
              [attr.aria-label]="
                !showNewPassword ? 'Show password' : 'Hide password'
              "
            >
              <span class="material-icons">{{
                !showNewPassword ? "visibility" : "visibility_off"
              }}</span>
            </button>
          </div>
          <span class="password-helper-label">
            Password is case sensitive and must be at least 8 characters
            long.<br />
            At least 1 <b>lowercase</b> letter.<br />
            At least 1 <b>uppercase</b> letter.<br />
            At least 1 <b>numeric</b> character.<br />
            At least 1 <b>special</b> character.
          </span>
          <div
            *ngIf="
              newPassword.controls['newPassword']?.invalid &&
              (newPassword.controls['newPassword'].dirty ||
                newPassword.controls['newPassword'].touched)
            "
          >
            <div
              *ngIf="newPassword.controls['newPassword'].errors?.['required']"
              class="error-message"
            >
              New Password is required.
            </div>
            <div
              class="error-message"
              *ngIf="newPassword.controls['newPassword']?.dirty"
            >
              <span
                class="error-message"
                *ngIf="!newPassword.controls['newPassword'].value?.match(passwordRegx) && !newPassword.controls['newPassword'].errors?.['required']"
                >Password does not meet the requirements.</span
              >
            </div>
          </div>
        </div>
        <div class="form-field settings-password-field">
          <label class="form-label" for="confirmNewPassword"
            >Confirm New Password<span class="req">*</span></label
          >
          <div class="settings-password-input">
            <input
              id="confirmNewPassword"
              class="input"
              [type]="showConfirmPassword ? 'text' : 'password'"
              formControlName="confirmNewPassword"
              maxlength="100"
              data-testid="settings-confirm-password"
              (blur)="trimConfirmPassword()"
            />
            <button
              type="button"
              class="settings-show-password"
              data-testid="settings-confirm-password-toggle"
              (click)="toggleConfirmPasswordVisibility()"
              [attr.aria-label]="
                !showConfirmPassword ? 'Show password' : 'Hide password'
              "
            >
              <span class="material-icons">{{
                !showConfirmPassword ? "visibility" : "visibility_off"
              }}</span>
            </button>
          </div>
          <div
            *ngIf="
              newPassword.controls['confirmNewPassword']?.invalid &&
              (newPassword.controls['confirmNewPassword'].dirty ||
                newPassword.controls['confirmNewPassword'].touched)
            "
          >
            <span
              class="error-message"
              *ngIf="newPassword.controls.confirmNewPassword.errors?.['required']"
            >
              Please confirm your new password.
            </span>
          </div>
          <span
            class="error-message"
            *ngIf="
              newPassword.controls['confirmNewPassword'].value != '' &&
              newPassword.controls['confirmNewPassword'].value !=
                newPassword.controls['newPassword'].value
            "
          >
            Passwords do not match.
          </span>
        </div>
        <span class="form-label settings-required-hint"
          ><span class="req">*</span>Indicates a required field.</span
        >
      </div>
    </form>
    <button
      type="button"
      class="btn btn--primary"
      (click)="saveNewPassword()"
      [disabled]="isSubmittingPassword"
      data-testid="settings-update-password"
    >
      <span class="material-icons">save</span> Update
    </button>
  </section>

  <!-- Out of Office -->
  <section class="card settings-card" data-testid="settings-ooo-card">
    <div class="settings-card__header">
      <h2 class="settings-card__title">Out of Office</h2>
    </div>
    <div class="settings-ooo-toggle">
      <mat-slide-toggle
        [(ngModel)]="editOutOfOfficeToggle"
        (toggleChange)="editOutOfOffice()"
        color="secondary"
        data-testid="settings-ooo-toggle"
        >Out of Office</mat-slide-toggle
      >
    </div>
    <p class="settings-ooo-hint">
      While you're marked out of office, you won't receive new Quote Requests or
      Work Assignments. Turn it on and set your start and end dates below — we'll
      pause new work during that window and resume it automatically when you're
      back.
    </p>
    <div class="settings-ooo-dates">
      <div class="form-field">
        <label class="form-label">Start Date</label>
        <div
          class="settings-ooo-field"
          [class.settings-ooo-field--disabled]="!editOutOfOfficeToggle"
        >
          <input
            class="settings-ooo-field__input"
            [matDatepicker]="oooStartPicker"
            [(ngModel)]="outOfOfficeStartDate"
            (dateChange)="saveOutOfOffice()"
            [disabled]="!editOutOfOfficeToggle"
            data-testid="settings-ooo-start-date"
            placeholder="mm/dd/yyyy"
            readonly
          />
          <mat-datepicker-toggle
            class="settings-ooo-field__toggle"
            data-testid="settings-ooo-start-date-toggle"
            [for]="oooStartPicker"
            [disabled]="!editOutOfOfficeToggle"
          ></mat-datepicker-toggle>
          <mat-datepicker #oooStartPicker></mat-datepicker>
        </div>
      </div>
      <div class="form-field">
        <label class="form-label">End Date</label>
        <div
          class="settings-ooo-field"
          [class.settings-ooo-field--disabled]="!editOutOfOfficeToggle"
        >
          <input
            class="settings-ooo-field__input"
            [matDatepicker]="oooEndPicker"
            [(ngModel)]="outOfOfficeEndDate"
            (dateChange)="saveOutOfOffice()"
            [disabled]="!editOutOfOfficeToggle"
            data-testid="settings-ooo-end-date"
            placeholder="mm/dd/yyyy"
            readonly
          />
          <mat-datepicker-toggle
            class="settings-ooo-field__toggle"
            data-testid="settings-ooo-end-date-toggle"
            [for]="oooEndPicker"
            [disabled]="!editOutOfOfficeToggle"
          ></mat-datepicker-toggle>
          <mat-datepicker #oooEndPicker></mat-datepicker>
        </div>
      </div>
    </div>
  </section>
  </div>

  <!-- Security (Keep Me Signed In devices) -->
  <section class="card settings-card security-card" data-testid="settings-security-card">
    <div class="settings-card__header">
      <h2 class="settings-card__title">Security</h2>
    </div>
    <!--
      Stated here, on the device list, rather than on the Keep Me Signed In
      toggle below: this is the list the sentence is about, and it is what the
      reader is looking at when they wonder why a device disappeared.
      <p>
      It names the CONTROL on this card — "Revoke" is the per-device button
      below — rather than the abstract act of signing out, which named neither
      that button nor the "Log Out of All Devices" one at the foot of the
      section.
      <p>
      Both claims match what connect-service actually does. `SessionResource
      .logout` is current-device-only by design: it hard-deletes only the
      current (userEmail, deviceId) refresh chain and its TrustedDevice row via
      `RefreshTokenHandler.revokeDevice`, which is why logging out really does
      revoke this device and drop it off the list, and why the user's other
      trusted devices stay signed in. Note the untrusted-session case revokes
      nothing server-side — there is no row to delete — so nothing is claimed
      about devices that were never trusted.
    -->
    <p class="security-card__hint">
      These are the devices currently signed in to your account. Revoke any you
      don’t recognize — revoking a device signs you out on that device only, and
      your other devices stay signed in. Logging out automatically revokes the
      device you are using now, so it will drop off this list.
    </p>

    <!-- Keep me signed in on this device (awareness path B) -->
    <div
      class="security-trust"
      *ngIf="showTrustToggle"
      data-testid="settings-trust-panel"
    >
      <div class="security-trust__copy">
        <span class="material-icons">verified_user</span>
        <span>Keep me signed in on this device so you don’t have to log in each time.</span>
      </div>
      <button
        type="button"
        class="btn btn--primary"
        (click)="trustThisDevice()"
        data-testid="settings-trust-device"
      >
        Keep Me Signed In
      </button>
    </div>

    <!-- Empty state — shown only when the enable toggle isn't the CTA (e.g. INTERNAL) -->
    <p
      class="security-empty"
      *ngIf="!isLoadingDevices && devices.length === 0 && !showTrustToggle"
      data-testid="settings-devices-empty"
    >
      No devices are signed in to your account.
    </p>

    <!-- Device list -->
    <ul class="device-list" *ngIf="devices.length > 0">
      <li
        class="device-row"
        *ngFor="let device of devices"
        data-testid="settings-device-row"
      >
        <div class="device-row__info">
          <span class="device-row__label" data-testid="settings-device-label">{{
            device.label
          }}</span>
          <span
            class="device-row__badge"
            *ngIf="device.isCurrent"
            data-testid="settings-this-device-badge"
            >This device</span
          >
          <span class="device-row__meta"
            >Last active {{ formatLastActive(device.lastUsedAt) }}</span
          >
        </div>
        <button
          type="button"
          class="btn btn--danger device-row__revoke"
          (click)="askRevokeDevice(device)"
          data-testid="settings-device-revoke"
        >
          Revoke
        </button>
      </li>
    </ul>

    <!--
      Subtle "couldn't refresh" hint — shown ONLY when a refresh failed while we
      already had devices listed (so Retry is meaningful). Never shown on the
      never-enabled path, which falls through to the enable toggle instead.
    -->
    <p
      class="security-stale"
      *ngIf="!isLoadingDevices && devicesStale"
      data-testid="settings-devices-stale"
    >
      <span class="security-stale__msg"
        >We couldn’t refresh your devices just now — this list may be out of
        date.</span
      >
      <button
        type="button"
        class="security-stale__retry"
        (click)="loadDevices()"
        data-testid="settings-devices-retry"
      >
        Retry
      </button>
    </p>

    <div class="security-actions" *ngIf="devices.length > 0">
      <button
        type="button"
        class="btn btn--secondary"
        (click)="askLogoutAllDevices()"
        data-testid="settings-logout-all"
      >
        <span class="material-icons">logout</span> Log Out of All Devices
      </button>
    </div>
  </section>

  <!-- Notifications -->
  <div class="settings-section-header">
    <h2 class="settings-section-header__title">Notifications</h2>
    <p class="settings-section-header__hint">
      Please select which notifications you would like to receive.
    </p>
  </div>
  <div class="notif-grid">
    <!-- Delivery channels (In-app / Email / SMS + opt-in/opt-out) -->
    <section class="card notif-card" data-testid="settings-notif-delivery-card">
      <div class="notif-card__header">
        <h3 class="notif-card__title">Notify me thru...</h3>
      </div>
      <div class="notif-checkbox notif-checkbox--select-all">
        <input
          type="checkbox"
          id="notif-delivery-select-all"
          class="notif-checkbox-field"
          [(ngModel)]="allNotifiedByEnabled"
          (change)="handleSelectAllNotifiedBy()"
          data-testid="settings-notif-delivery-select-all"
        />
        <label for="notif-delivery-select-all">Select all</label>
      </div>
      <div class="notif-divider"></div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-delivery-in-app"
          class="notif-checkbox-field"
          [(ngModel)]="user.notifyByInApp"
          data-testid="settings-notif-delivery-in-app"
          (change)="handleNotifiedByChange()"
        />
        <label for="notif-delivery-in-app">In-app Notifications</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-delivery-email"
          class="notif-checkbox-field"
          [(ngModel)]="user.notifyByEmail"
          data-testid="settings-notif-delivery-email"
          (change)="handleNotifiedByChange()"
        />
        <label for="notif-delivery-email">Email Notifications</label>
      </div>
      <div class="notif-checkbox" *ngIf="user.phone || user.phone2">
        <input
          type="checkbox"
          id="notif-delivery-sms"
          class="notif-checkbox-field"
          [(ngModel)]="user.notifyBySms"
          data-testid="settings-notif-delivery-sms"
          (change)="handleNotifiedByChange()"
        />
        <label for="notif-delivery-sms">SMS Notifications</label>
        <span class="notif-sms-status" *ngIf="user.notifyBySms">
          <span
            class="material-icons"
            *ngIf="user.optInStatus === 'PENDING'"
            style="color: var(--text-gray)"
            >pending</span
          >
          <span
            class="material-icons"
            *ngIf="user.optInStatus === 'ERROR'"
            style="color: var(--error)"
            >error</span
          >
          <span
            class="material-icons"
            *ngIf="user.optInStatus === 'OPTED_IN'"
            style="color: var(--ready)"
            >check_circle</span
          >
          <span
            class="material-icons"
            *ngIf="user.optInStatus === 'OPTED_OUT'"
            style="color: var(--error-dark)"
            >phone_disabled</span
          >
        </span>
      </div>
      <div
        class="notif-sms-panel"
        *ngIf="
          (user.phone || user.phone2) &&
          user.notifyBySms &&
          (user.optInStatus === 'NOT_OPTED_IN' ||
            user.optInStatus === 'OPTED_IN' ||
            user.optInStatus === 'PENDING' ||
            user.optInStatus === 'ERROR' ||
            user.optInStatus === 'OPTED_OUT')
        "
      >
        <div
          *ngIf="
            user.optInStatus === 'NOT_OPTED_IN' ||
            user.optInStatus === 'ERROR' ||
            user.optInStatus === 'OPTED_OUT'
          "
        >
          <button
            type="button"
            class="btn btn--secondary"
            (click)="toggleOptInDialog()"
            data-testid="settings-sms-opt-in"
          >
            <span class="material-icons">sms</span> Opt-In
          </button>
          <span class="sms-label" *ngIf="user.optInStatus === 'NOT_OPTED_IN'"
            >You need to opt in before we can send you SMS
            notificaitons.</span
          >
          <span
            class="sms-label"
            *ngIf="user.optInStatus === 'ERROR'"
            style="color: var(--error)"
            >We couldn’t complete your opt-in. Please try it again.</span
          >
          <span
            class="sms-label"
            *ngIf="user.optInStatus === 'OPTED_OUT'"
            style="color: var(--error-dark)"
          >
            You have previously opted out. You need to opt in again before we
            can send you SMS notifications.
          </span>
        </div>
        <div *ngIf="user.optInStatus === 'PENDING'">
          <button
            type="button"
            class="btn btn--secondary"
            (click)="toggleOptInDialog()"
            data-testid="settings-sms-opt-in"
          >
            <span class="material-icons">phone_forwarded</span> Resend
          </button>
          <span class="sms-label"
            >We’re waiting for your confirmation before we start sending you
            SMS notifications.</span
          >
        </div>
        <div *ngIf="user.optInStatus === 'OPTED_IN'">
          <button
            type="button"
            class="btn btn--danger"
            (click)="toggleOptOutDialog()"
            data-testid="settings-sms-opt-out"
          >
            <span class="material-icons">feedback</span> Opt-Out
          </button>
          <span class="sms-label"
            >Click the button above if you'd like to opt out from SMS
            notifications.</span
          >
        </div>
      </div>
      <button
        type="button"
        class="btn btn--primary notif-card__save"
        (click)="saveNotifiedBy()"
        [disabled]="isSavingSettings"
        data-testid="settings-notif-delivery-save"
      >
        <span class="material-icons">save</span> Save
      </button>
    </section>

    <!-- Invitations -->
    <section class="card notif-card" data-testid="settings-notif-invitations-card">
      <div class="notif-card__header">
        <h3 class="notif-card__title">Invitations</h3>
      </div>
      <div class="notif-checkbox notif-checkbox--select-all">
        <input
          type="checkbox"
          id="notif-invitations-select-all"
          class="notif-checkbox-field"
          [(ngModel)]="allInvitationNotificationsEnabled"
          data-testid="settings-notif-invitations-select-all"
          (change)="handleSelectAllInvitationNotifications()"
        />
        <label for="notif-invitations-select-all">All Notifications</label>
      </div>
      <div class="notif-divider"></div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-invitations-new"
          class="notif-checkbox-field"
          [(ngModel)]="user.newInvitationNotification"
          data-testid="settings-notif-invitations-new"
          (change)="handleInvitationNotificationChange()"
        />
        <label for="notif-invitations-new">New Invitations</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-invitations-updates"
          class="notif-checkbox-field"
          [(ngModel)]="user.projectUpdateNotification"
          data-testid="settings-notif-invitations-updates"
          (change)="handleInvitationNotificationChange()"
        />
        <label for="notif-invitations-updates">Project Updates</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-invitations-cancelled"
          class="notif-checkbox-field"
          [(ngModel)]="user.cancelledInvitationNotification"
          data-testid="settings-notif-invitations-cancelled"
          (change)="handleInvitationNotificationChange()"
        />
        <label for="notif-invitations-cancelled">Cancelled</label>
      </div>
      <button
        type="button"
        class="btn btn--primary notif-card__save"
        (click)="saveInvitationNotifications()"
        [disabled]="isSavingSettings"
        data-testid="settings-notif-invitations-save"
      >
        <span class="material-icons">save</span> Save
      </button>
    </section>

    <!-- Pending -->
    <section class="card notif-card" data-testid="settings-notif-pending-card">
      <div class="notif-card__header">
        <h3 class="notif-card__title">Pending</h3>
      </div>
      <div class="notif-checkbox notif-checkbox--select-all">
        <input
          type="checkbox"
          id="notif-pending-select-all"
          class="notif-checkbox-field"
          [(ngModel)]="allProposalNotifcationsEnabled"
          data-testid="settings-notif-pending-select-all"
          (change)="handleSelectAllProposalNotifications()"
        />
        <label for="notif-pending-select-all">All Notifications</label>
      </div>
      <div class="notif-divider"></div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-pending-awarded"
          class="notif-checkbox-field"
          [(ngModel)]="user.awardedNotification"
          data-testid="settings-notif-pending-awarded"
          (change)="handleProposalNotificationChange()"
        />
        <label for="notif-pending-awarded">Awarded</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-pending-closed"
          class="notif-checkbox-field"
          [(ngModel)]="user.closedNotification"
          data-testid="settings-notif-pending-closed"
          (change)="handleProposalNotificationChange()"
        />
        <label for="notif-pending-closed">Closed</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-pending-cancelled"
          class="notif-checkbox-field"
          [(ngModel)]="user.cancelledProposalNotification"
          data-testid="settings-notif-pending-cancelled"
          (change)="handleProposalNotificationChange()"
        />
        <label for="notif-pending-cancelled">Cancelled</label>
      </div>
      <button
        type="button"
        class="btn btn--primary notif-card__save"
        (click)="saveProposalNotifications()"
        [disabled]="isSavingSettings"
        data-testid="settings-notif-pending-save"
      >
        <span class="material-icons">save</span> Save
      </button>
    </section>

    <!-- Active Projects -->
    <section
      class="card notif-card"
      data-testid="settings-notif-active-projects-card"
    >
      <div class="notif-card__header">
        <h3 class="notif-card__title">Active Projects</h3>
      </div>
      <div class="notif-checkbox notif-checkbox--select-all">
        <input
          type="checkbox"
          id="notif-active-select-all"
          class="notif-checkbox-field"
          [(ngModel)]="allActiveProjectNotificationsEnabled"
          data-testid="settings-notif-active-select-all"
          (change)="handleSelectAllActiveProjectNotifications()"
        />
        <label for="notif-active-select-all">All Notifications</label>
      </div>
      <div class="notif-divider"></div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-active-revisions"
          class="notif-checkbox-field"
          [(ngModel)]="user.revisionRequestedNotification"
          data-testid="settings-notif-active-revisions"
          (change)="handleActiveProjectNotificationChange()"
        />
        <label for="notif-active-revisions">Revisions Requested</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-active-completed"
          class="notif-checkbox-field"
          [(ngModel)]="user.completedNotification"
          data-testid="settings-notif-active-completed"
          (change)="handleActiveProjectNotificationChange()"
        />
        <label for="notif-active-completed">Completed</label>
      </div>
      <div class="notif-checkbox">
        <input
          type="checkbox"
          id="notif-active-cancelled"
          class="notif-checkbox-field"
          [(ngModel)]="user.cancelledActiveProjectNotification"
          data-testid="settings-notif-active-cancelled"
          (change)="handleActiveProjectNotificationChange()"
        />
        <label for="notif-active-cancelled">Cancelled</label>
      </div>
      <button
        type="button"
        class="btn btn--primary notif-card__save"
        (click)="saveActiveProjectNotifications()"
        [disabled]="isSavingSettings"
        data-testid="settings-notif-active-projects-save"
      >
        <span class="material-icons">save</span> Save
      </button>
    </section>
  </div>
</div>

<opt-in-dialog
  [active]="showOptInDialog"
  (modalClosed)="toggleOptInDialog()"
  (handleOptIn)="optInToSms($event)"
/>

<opt-out-dialog
  [active]="showOptOutDialog"
  (modalClosed)="toggleOptOutDialog()"
  (handleOptOut)="optOutFromSms($event)"
/>

<!-- Revoke device confirmation -->
<app-modal
  [open]="showRevokeModal"
  [name]="'revoke-device'"
  (close)="cancelRevokeDevice()"
>
  <div modal-header>Remove this device?</div>
  <div modal-body>
    <p>
      {{
        deviceToRevoke?.isCurrent
          ? "This is the device you're using right now. Removing it will sign you out."
          : "This device will be signed out and will need to log in again."
      }}
    </p>
  </div>
  <button
    modal-footer
    type="button"
    class="btn btn--danger"
    data-testid="revoke-device-confirm"
    (click)="confirmRevokeDevice()"
  >
    Remove
  </button>
  <button
    modal-footer
    type="button"
    class="btn btn--secondary"
    data-testid="revoke-device-cancel"
    (click)="cancelRevokeDevice()"
  >
    Cancel
  </button>
</app-modal>

<!-- Log out of all devices confirmation -->
<app-modal
  [open]="showLogoutAllModal"
  [name]="'logout-all-devices'"
  (close)="cancelLogoutAllDevices()"
>
  <div modal-header>Log out of all devices?</div>
  <div modal-body>
    <p>
      Every device signed in to your account — including this one — will be
      signed out. You’ll need to log in again.
    </p>
  </div>
  <button
    modal-footer
    type="button"
    class="btn btn--danger"
    data-testid="logout-all-devices-confirm"
    (click)="confirmLogoutAllDevices()"
  >
    Log out everywhere
  </button>
  <button
    modal-footer
    type="button"
    class="btn btn--secondary"
    data-testid="logout-all-devices-cancel"
    (click)="cancelLogoutAllDevices()"
  >
    Cancel
  </button>
</app-modal>
